import { displayPlayerName } from 'src/app/core/util/player-name/player-name.util';
import { Injectable, NgZone } from '@angular/core';
import { MyPlayerService } from './my-player.service';
import { LobbyBackendService } from 'src/app/core/backend-services/lobby/lobby.backend-service';
import { SocketIoService, MdbToastService } from 'src/app/core/services';
import { UserBackendService } from 'src/app/core/backend-services';
import { TableStatusEnum } from 'src/app/core/enums';
import { UserInfoInterface } from 'src/app/core/interfaces';
import { AppModule } from 'src/app/app.module';
import { MatchmakingService } from './matchmaking.service';

@Injectable({
  providedIn: 'root',
})
export class LobbyService {
  static readonly PRIVATE_TABLE_MESSAGE = 'Das ist ein privater Tisch. Hier können sich nur Freunde dazusetzen.';

  private isInitialized: boolean = false;
  private _tables: any[] = [];
  private _playersOnline: UserInfoInterface[] = [];
  private missingRetries = 0;
  /** online-list entries by userID (friend / block flags for lobby tiles + table sheet) */
  private _onlineByID = new Map<number, UserInfoInterface>();

  public currentRoom: 'standard' | 'training' | 'gambling' = 'standard';
  public filterNumPlayers: 2 | 3 | 4 = 2;
  public isCreatingTable: boolean = false;
  public isLeavingTable: boolean = false;

  constructor(
    private lobbyBackendService: LobbyBackendService,
    private myPlayer: MyPlayerService,
    private socketIO: SocketIoService,
    private userBackendService: UserBackendService,
    private toast: MdbToastService,
    private matchmakingService: MatchmakingService,
    private zone: NgZone
  ) {}

  public async init(): Promise<void> {
    await this.myPlayer.waitForInit();

    await this.update();
    await this.updateOnlineList();
    this.startOnlinePolling();

    if (this.isInitialized) return;
    this.isInitialized = true;

    this.socketIO.on('lobbyUpdate', async (data) => {
      data = data.data;
      if (data.newTable) {
        this._tables.push(data.newTable);
      }
      if (data.deletedTable) {
        this._tables.splice(
          this._tables.map((el) => el.id).indexOf(data.deletedTable),
          1
        );
      }
      if (data.table) {
        if (
          data.table.members.filter((el) => el && !!el.userID).length ===
          data.table.maxPlayers
        ) {
          data.table.status = TableStatusEnum.RUNNING;
        }
        this._tables[this._tables.map((el) => el.id).indexOf(data.table.id)] =
          data.table;
      }
    });
  }

  public async update(): Promise<void> {
    try {
      const tables = await this.lobbyBackendService.getTables();
      this.zone.run(() => (this._tables = tables));
    } catch (e) {
      console.warn(e);
    }
  }

  /** the online list every 20 s; stopped by a 401, started again with the next init */
  private onlinePolling: ReturnType<typeof setInterval> | undefined;

  private startOnlinePolling(): void {
    if (this.onlinePolling) return;
    this.onlinePolling = setInterval(() => {
      this.updateOnlineList();
    }, 20000);
  }

  public async updateOnlineList(): Promise<void> {
    try {
      const players = await this.userBackendService.getCurrentlyOnlinePlayers();
      // The first load runs after the socket start and the 20 s timer may run outside
      // Angular's zone; inside it the views show the new list right away, not on the next click.
      this.zone.run(() => {
        this._playersOnline = players;
        this.sortPlayersOnline();
        this._onlineByID = new Map(this._playersOnline.map((el) => [el.userID, el]));
        this.retryIfIAmMissing();
      });
    } catch (e: any) {
      // logged out (an expired session in a tab left open): asking every 20 s only collects
      // 401s; a new init (back in the lobby after logging in) starts the list again
      if (e?.status === 401) {
        clearInterval(this.onlinePolling);
        this.onlinePolling = undefined;
        return;
      }
      console.warn(e);
    }
  }

  /**
   * Right after entering, the server may not list me as online yet; then the list stayed
   * empty or without me until the next 20 s refresh. Ask again shortly, a few times at most.
   */
  private retryIfIAmMissing(): void {
    const myID = this.myPlayer.player?.userID;
    if (!myID || this._onlineByID.has(myID)) {
      this.missingRetries = 0;
      return;
    }
    if (this.missingRetries >= 3) return;
    this.missingRetries++;
    setTimeout(() => void this.updateOnlineList(), 2000);
  }

  public get tables() {
    return this._tables;
  }

  public get playersOnline(): UserInfoInterface[] {
    return this._playersOnline;
  }

  public async joinAsSpectator(tableID: number): Promise<void> {
    try {
      await this.socketIO.post('game/watch', {
        tableID: tableID,
      });
    } catch (e) {
      console.warn('game/watch failed:', e);
    }
  }

  public async joinTable(tableID: number): Promise<void> {
    try {
      const table = await this.socketIO.post('game/table/join', {
        tableID: tableID,
      });
      // no need to set table, this is done in tableUpdate handler
      //await this.myPlayer.setMyTable(table);
    } catch (e) {
      if (e === 'userIsEnemyOfARoomMember')
        this.toast.warning(this.blockedJoinMessage(tableID));
      else if (e === 'tableIsPrivateAndUserNotFriend')
        this.toast.warning(LobbyService.PRIVATE_TABLE_MESSAGE);
      else if (e === 'tableHasGuest')
        this.toast.warning(
          'Du kannst dich nicht dazusetzen, weil an diesem Tisch ein Gast sitzt. Du hast in den Einstellungen „Nicht gegen Gäste spielen“ aktiviert.'
        );
      else if (e === 'tableBlocksGuests')
        this.toast.warning(
          'An diesem Tisch spielen nur registrierte Spieler. Registriere dich kostenlos, um dich dazuzusetzen.'
        );
      console.warn('game/table/join failed:', e);
    }
  }

  public async createTable(numPlayers: number = 2): Promise<void> {
    if (this.isCreatingTable) return;

    this.isCreatingTable = true;

    try {
      const table = await this.socketIO.post('game/table/create', {
        numPlayers: numPlayers,
      });
      await this.myPlayer.setMyTable(table);
      await this.update();
    } catch (e) {
      this.toast.warning('Tisch konnte nicht erstellt werden.');
      console.warn(e);
    } finally {
      this.isCreatingTable = false;
    }
  }

  public async createPrivateTable(numPlayers: number = 2): Promise<void> {
    try {
      const table = await this.socketIO.post('game/table/create', {
        numPlayers: numPlayers,
        private: true,
      });
      await this.myPlayer.setMyTable(table);
      await this.update();
    } catch (e) {
      this.toast.warning('Privater Tisch konnte nicht erstellt werden.');
      console.warn(e);
    }
  }

  public async leaveTable(): Promise<void> {
    if (this.isLeavingTable) return;

    this.isLeavingTable = true;

    try {
      await this.socketIO.post('game/table/leave');
      await this.myPlayer.setMyTable(undefined);
      await this.update();
    } catch (e) {
      console.warn('game/table/leave failed:', e);
    } finally {
      this.isLeavingTable = false;
    }

    // Also leave matchmaking if currently in queue
    if (this.matchmakingService.state.isInQueue) {
      await this.matchmakingService.leaveQueue();
    }
  }

  /**
   * tables of the current game type, free seats first: my own table, then tables I
   * can sit down at, then open tables closed to me (block / private), then full or
   * running ones. Within a group the server order stays (stable sort).
   */
  public getTablesList(): any[] {
    return this.tables
      .filter((el) => el.maxPlayers === this.filterNumPlayers)
      .map((table, index) => ({ table, index, rank: this.tableSortRank(table) }))
      .sort((a, b) => a.rank - b.rank || a.index - b.index)
      .map((el) => el.table);
  }

  private tableSortRank(table: any): number {
    const members: any[] = table?.members ?? [];
    if (members.some((el) => this.isMe(el?.userID))) return 0;
    const hasFreeSeat =
      table?.status === TableStatusEnum.OPENED &&
      members.filter((el) => !!el?.userID).length < table.maxPlayers;
    if (!hasFreeSeat) return 3;
    if (this.hasBlockAtTable(table) || this.isPrivateTableClosedToMe(table)) return 2;
    return 1;
  }

  public async moveToTrainingRoom(): Promise<void> {
    if (this.currentRoom === 'training') return;
    this.currentRoom = 'training';
  }

  public async moveToStandardRoom(): Promise<void> {
    if (this.currentRoom === 'standard') return;
    this.currentRoom = 'standard';
  }

  /** names the players behind a refused join, as far as the online list knows them */
  private blockedJoinMessage(tableID: number): string {
    const memberIDs: number[] = (
      this._tables.find((el) => el.id === tableID)?.members ?? []
    ).map((el) => el?.userID);
    const members = this._playersOnline.filter((el) =>
      memberIDs.includes(el.userID)
    );
    const names = (list: UserInfoInterface[]) =>
      list.map((el) => displayPlayerName(el)).join(' und ');
    const blockedMe = members.filter((el) => el.hasEnemiedMe);
    const blockedByMe = members.filter((el) => el.isEnemy && !el.hasEnemiedMe);
    const reasons = [
      ...(blockedMe.length
        ? [
            `${names(blockedMe)} dich gesperrt ${
              blockedMe.length === 1 ? 'hat' : 'haben'
            }`,
          ]
        : []),
      ...(blockedByMe.length
        ? [`du ${names(blockedByMe)} gesperrt hast`]
        : []),
    ];
    return reasons.length
      ? `Du kannst dich nicht dazusetzen, weil ${reasons.join(' und ')}.`
      : 'Du kannst dich nicht dazusetzen, weil zwischen dir und einem Spieler am Tisch eine Sperre besteht.';
  }

  /** true for my own userID (lobby tiles mark my seat with "Du") */
  public isMe(userID: number | undefined): boolean {
    return !!userID && userID === this.myPlayer.player?.userID;
  }

  /**
   * friend flag for a seated player. Seated players are online, so the online list
   * (refreshed every 20s, carries `isFriend`) is the cheap source — no extra request.
   */
  public isFriend(userID: number | undefined): boolean {
    return !!userID && !!this._onlineByID.get(userID)?.isFriend;
  }

  /** true if a block exists between me and any seated member of the table */
  public hasBlockAtTable(table: any): boolean {
    return (table?.members ?? []).some((el: any) => {
      const entry = el?.userID ? this._onlineByID.get(el.userID) : undefined;
      return !!entry && (entry.isEnemy || entry.hasEnemiedMe);
    });
  }

  /**
   * true if the table is private and its creator is known not to be my friend. Someone
   * missing from the online list stays joinable: the server decides, the toast explains.
   */
  public isPrivateTableClosedToMe(table: any): boolean {
    if (!table?.private) return false;
    const creatorID = (table.members ?? []).find((el: any) => el?.userID > 0)?.userID;
    if (!creatorID || this.isMe(creatorID)) return false;
    const creator = this._onlineByID.get(creatorID);
    return !!creator && !creator.isFriend;
  }

  public sitsOnTable(): boolean {
    return this.myPlayer.player && !!this.myPlayer.player.tableID;
  }

  public async changeFilterNumPlayers(v: 2 | 3 | 4): Promise<void> {
    this.filterNumPlayers = v;
  }

  private sortPlayersOnline(): void {
    this._playersOnline.sort(
      (
        a,
        b // sort alphabetically
      ) =>
        a.username.localeCompare(b.username, undefined, {
          sensitivity: 'base',
        })
    );
    this._playersOnline.sort((a, b) => {
      // sort by isEnemy
      if (a.isEnemy && !b.isEnemy) {
        return -1;
      } // a davor
      if (!a.isEnemy && b.isEnemy) {
        return 1;
      } // a danach
      return 0; // gleich
    });
    this._playersOnline.sort((a, b) => {
      // sort by isFriend
      if (a.isFriend && !b.isFriend) {
        return -1;
      } // a davor
      if (!a.isFriend && b.isFriend) {
        return 1;
      } // a danach
      return 0; // gleich
    });
    this._playersOnline.sort((a, b) => {
      // sort by isInGame
      if (a.isInGame && !b.isInGame) {
        return 1;
      } // a danach
      if (!a.isInGame && b.isInGame) {
        return -1;
      } // a davor
      return 0; // gleich
    });
    const myListEntry = this._playersOnline.find(
      (el) => el.userID === this.myPlayer.player.userID
    ); // filter out my entry
    this._playersOnline = this._playersOnline.filter(
      (el) => el.userID !== this.myPlayer.player.userID
    ); // filter out my entry
    if (myListEntry) this._playersOnline.unshift(myListEntry); // add my entry to beginning
  }
}
